<template>
    <BaseDialog
        v-model:open="dialogOpen"
        :title="t('onboarding.micPermission.title')"
        width="narrow"
        :show-close="false"
        :reserve-mobile-nav-space="false"
        :on-interact-outside="handleInteractOutside">
        <!-- Content-click delegation preserves chat/profile callers; the text-mode action stops propagation. -->
        <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
        <div class="flex flex-col gap-4" @click="emit('click', $event)">
            <div class="mic-permission-aide-image">
                <svg aria-hidden="true" class="h-full w-full" viewBox="0 0 381 227" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0.625977" y="43" width="369" height="180" fill="#F0F2F9" />
                    <rect x="0.625977" width="369" height="43" fill="#E8EAED" />
                    <rect x="6.62598" y="7" width="8" height="8" rx="4" fill="#D9D9D9" fill-opacity="0.5" />
                    <rect x="18.626" y="7" width="8" height="8" rx="4" fill="#D9D9D9" fill-opacity="0.5" />
                    <rect x="29.626" y="7" width="8" height="8" rx="4" fill="#D9D9D9" fill-opacity="0.5" />
                    <rect x="0.625977" y="22" width="369" height="21" fill="white" />
                    <rect x="45.626" y="3" width="122" height="35" rx="10" fill="white" />
                    <rect x="50.626" y="8" width="12" height="12" rx="6" fill="#D9D9D9" fill-opacity="0.5" />
                    <rect x="65.626" y="10" width="94" height="8" rx="4" fill="#D9D9D9" fill-opacity="0.5" />
                    <rect x="43.626" y="27" width="12" height="12" rx="6" fill="#D9D9D9" fill-opacity="0.5" />
                    <rect x="349.626" y="27" width="12" height="12" rx="6" fill="#D9D9D9" fill-opacity="0.5" />
                    <rect x="63.626" y="27" width="260" height="12" rx="6" fill="#D9D9D9" fill-opacity="0.5" />
                    <rect x="24.626" y="27" width="12" height="12" rx="6" fill="#D9D9D9" fill-opacity="0.5" />
                    <rect x="330.626" y="27" width="12" height="12" rx="6" fill="#D9D9D9" fill-opacity="0.5" />
                    <rect x="6.62598" y="27" width="12" height="12" rx="6" fill="#D9D9D9" fill-opacity="0.5" />
                    <rect x="89.626" y="51" width="170" height="106" fill="white" />
                    <rect x="172.626" y="133" width="33" height="17" fill="#D9D9D9" fill-opacity="0.5" />
                    <rect x="211.626" y="133" width="37" height="17" fill="#005EEA" />
                    <rect x="98.626" y="59" width="146" height="10" rx="5" fill="#D9D9D9" fill-opacity="0.5" />
                    <rect x="98.626" y="74" width="146" height="10" rx="5" fill="#D9D9D9" fill-opacity="0.5" />
                    <rect x="98.626" y="94" width="92" height="10" rx="5" fill="#D9D9D9" fill-opacity="0.5" />
                    <rect x="98.626" y="108" width="92" height="10" rx="5" fill="#D9D9D9" fill-opacity="0.5" />
                </svg>
            </div>
            <div class="mic-permission-aide-text">
                <div class="body text-ink-muted">{{ t("onboarding.micPermission.instructions") }}</div>
            </div>
        </div>
        <template #footer
            ><BaseButton theme="tertiary" @click="handleDialogCancel">{{ t("onboarding.micPermission.ratherTypeButton") }}</BaseButton></template
        >
    </BaseDialog>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = withDefaults(defineProps<{ isOpen?: boolean }>(), { isOpen: false });
const emit = defineEmits<{ cancel: []; click: [event: MouseEvent] }>();
const dialogOpen = ref(props.isOpen);

watch(
    () => props.isOpen,
    (open) => {
        dialogOpen.value = open;
    },
);

function handleInteractOutside(event: Event) {
    if (event.defaultPrevented) {
        return;
    }
    if (event.type === "keydown" && (event as KeyboardEvent).key === "Escape") {
        emit("cancel");
        return;
    }
    const originalEvent = (event as CustomEvent<{ originalEvent: Event }>).detail?.originalEvent;
    if (originalEvent instanceof MouseEvent) {
        // Keep the outside pointer's default focus move from overriding the dialog's focus restoration.
        originalEvent.preventDefault();
        emit("click", originalEvent);
    }
}

function handleDialogCancel(e: MouseEvent) {
    e.stopPropagation();
    dialogOpen.value = false;
    emit("cancel");
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .mic-permission-aide-text {
        @apply text-center text-balance;
    }
}
</style>
